/**
 * @file hooks/useNotifications.ts
 * @brief Custom hooks for notifications functionality
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/api/notificationService';
import { QUERY_KEYS } from '../services/queryClient';

/**
 * Hook to fetch all notifications with pagination
 */
export function useNotifications(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [...QUERY_KEYS.notifications, page, pageSize],
    queryFn: () => notificationService.listNotifications(page, pageSize),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
}

/**
 * Hook to fetch unread notifications count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: QUERY_KEYS.unreadNotifications,
    queryFn: async () => {
      const response = await notificationService.getUnread();
      return response.count || 0;
    },
    staleTime: 15000, // Consider fresh for 15 seconds
    refetchInterval: 15000, // Refetch every 15 seconds for badge updates
  });
}

/**
 * Hook to fetch unread notifications
 */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: QUERY_KEYS.unreadNotifications,
    queryFn: () => notificationService.getUnread(),
    staleTime: 30000,
    refetchInterval: 30000,
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => 
      notificationService.markRead(notificationId),
    onSuccess: () => {
      // Invalidate and refetch notifications queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadNotifications });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      // Invalidate and refetch notifications queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadNotifications });
    },
  });
}

/**
 * Hook to clear/delete all read notifications
 */
export function useClearReadNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.clearReadNotifications(),
    onSuccess: () => {
      // Invalidate and refetch notifications queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadNotifications });
    },
  });
}

/**
 * Hook to manage notification preferences
 */
export function useNotificationPreferences() {
  const queryClient = useQueryClient();

  const { data: preferences, ...query } = useQuery({
    queryKey: QUERY_KEYS.notificationPreferences,
    queryFn: () => notificationService.getPreferences(),
  });

  const updatePreferences = useMutation({
    mutationFn: (newPreferences: Record<string, boolean>) =>
      notificationService.updatePreferences(newPreferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationPreferences });
    },
  });

  return {
    preferences,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
    ...query,
  };
}
